import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';


const BulkAssignModal = ({ selectedResources, onClose, onSuccess }) => {
  const [projects, setProjects] = useState([]);
  const [phases, setPhases] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedPhaseId, setSelectedPhaseId] = useState('');
  const [selectedDeliverableId, setSelectedDeliverableId] = useState('');
  const [assignmentType, setAssignmentType] = useState('phase'); // 'phase' or 'deliverable'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProjectId) {
      loadPhases(selectedProjectId);
      loadDeliverables(selectedProjectId);
    } else {
      setPhases([]);
      setDeliverables([]);
      setSelectedPhaseId('');
      setSelectedDeliverableId('');
    }
  }, [selectedProjectId]);

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const { data, error } = await supabase
        ?.from('projects')
        ?.select('id, project_name, project_number, status')
        ?.in('status', ['Planning', 'In Progress'])
        ?.order('project_name', { ascending: true });
      if (!error) setProjects(data || []);
    } catch (err) {
      console.error('Error loading projects:', err?.message);
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadPhases = async (projectId) => {
    try {
      const { data, error } = await supabase
        ?.from('project_phases')
        ?.select('id, name, start_date, end_date, status')
        ?.eq('project_id', projectId)
        ?.order('sort_order', { ascending: true });
      if (!error) setPhases(data || []);
    } catch (err) {
      console.error('Error loading phases:', err?.message);
    }
  };

  const loadDeliverables = async (projectId) => {
    try {
      const { data, error } = await supabase
        ?.from('deliverables')
        ?.select('id, name, start_date, end_date, phase_id')
        ?.eq('project_id', projectId)
        ?.eq('row_type', 'deliverable')
        ?.order('sort_order', { ascending: true });
      if (!error) setDeliverables(data || []);
    } catch (err) {
      console.error('Error loading deliverables:', err?.message);
    }
  };

  const handlePhaseSelect = (phaseId) => {
    setSelectedPhaseId(phaseId);
    const phase = phases?.find(p => p?.id === phaseId);
    if (phase?.start_date) setStartDate(phase?.start_date);
    if (phase?.end_date) setEndDate(phase?.end_date);
  };

  const handleDeliverableSelect = (deliverableId) => {
    setSelectedDeliverableId(deliverableId);
    const deliverable = deliverables?.find(d => d?.id === deliverableId);
    if (deliverable?.start_date) setStartDate(deliverable?.start_date);
    if (deliverable?.end_date) setEndDate(deliverable?.end_date);
  };

  const handleAssign = async () => {
    if (!selectedProjectId) {
      setError('Please select a project.');
      return;
    }
    if (assignmentType === 'phase' && !selectedPhaseId) {
      setError('Please select a phase.');
      return;
    }
    if (assignmentType === 'deliverable' && !selectedDeliverableId) {
      setError('Please select a deliverable.');
      return;
    }
    if (!startDate || !endDate) {
      setError('Please provide start and end dates.');
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setError('End date must be after start date.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const project = projects?.find(p => p?.id === selectedProjectId);
      const projectName = project?.project_name || project?.project_number || 'Project';

      if (assignmentType === 'phase') {
        const phase = phases?.find(p => p?.id === selectedPhaseId);
        const phaseName = phase?.name || '';

        // Create resource_assignments for each selected resource
        const assignmentInserts = selectedResources?.map(resource => ({
          resource_id: resource?.id,
          project_id: selectedProjectId,
          project_name: projectName,
          phase_name: phaseName,
          start_date: startDate,
          end_date: endDate,
          is_current: false,
          is_upcoming: true,
        }));

        const { error: insertError } = await supabase
          ?.from('resource_assignments')
          ?.insert(assignmentInserts);

        if (insertError) throw insertError;
      } else {
        // Assign to deliverable via deliverable_resource_assignments
        const deliverable = deliverables?.find(d => d?.id === selectedDeliverableId);

        // Insert into deliverable_resource_assignments for each resource
        const deliverableAssignmentInserts = selectedResources?.map(resource => ({
          deliverable_id: selectedDeliverableId,
          resource_id: resource?.id,
        }));

        const { error: deliverableError } = await supabase
          ?.from('deliverable_resource_assignments')
          ?.insert(deliverableAssignmentInserts);

        if (deliverableError) throw deliverableError;

        // Also create resource_assignments for timeline tracking
        const assignmentInserts = selectedResources?.map(resource => ({
          resource_id: resource?.id,
          project_id: selectedProjectId,
          project_name: projectName,
          phase_name: deliverable?.name || '',
          start_date: startDate,
          end_date: endDate,
          is_current: false,
          is_upcoming: true,
        }));

        const { error: insertError } = await supabase
          ?.from('resource_assignments')
          ?.insert(assignmentInserts);

        if (insertError) throw insertError;
      }

      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error('Error bulk assigning resources:', err?.message);
      setError(err?.message || 'Failed to assign resources. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredDeliverables = selectedPhaseId && assignmentType === 'deliverable'
    ? deliverables?.filter(d => d?.phase_id === selectedPhaseId)
    : deliverables;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="UserCheck" size={20} className="text-accent" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Bulk Assign Resources</h2>
              <p className="text-sm text-muted-foreground">
                Assigning {selectedResources?.length} resource{selectedResources?.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Selected Resources Preview */}
        <div className="px-6 pt-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Selected Resources</p>
          <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
            {selectedResources?.map(r => (
              <span key={r?.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-accent/10 text-accent text-xs font-medium">
                <Icon name="User" size={10} />
                {r?.name}
              </span>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          {/* Assignment Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Assign To</label>
            <div className="flex gap-2">
              <button
                onClick={() => { setAssignmentType('phase'); setSelectedDeliverableId(''); }}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                  assignmentType === 'phase' ?'bg-accent text-white border-accent' :'bg-background text-foreground border-border hover:bg-muted/50'
                }`}
              >
                Phase
              </button>
              <button
                onClick={() => { setAssignmentType('deliverable'); setSelectedPhaseId(''); }}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                  assignmentType === 'deliverable' ?'bg-accent text-white border-accent' :'bg-background text-foreground border-border hover:bg-muted/50'
                }`}
              >
                Deliverable
              </button>
            </div>
          </div>

          {/* Project Select */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Project <span className="text-destructive">*</span>
            </label>
            {loadingProjects ? (
              <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent"></div>
                Loading projects...
              </div>
            ) : (
              <select
                value={selectedProjectId}
                onChange={e => setSelectedProjectId(e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              >
                <option value="">Select a project...</option>
                {projects?.map(p => (
                  <option key={p?.id} value={p?.id}>
                    {p?.project_name} ({p?.project_number})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Phase Select (for phase assignment or deliverable filtering) */}
          {selectedProjectId && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Phase {assignmentType === 'phase' && <span className="text-destructive">*</span>}
                {assignmentType === 'deliverable' && <span className="text-muted-foreground text-xs ml-1">(optional, to filter deliverables)</span>}
              </label>
              <select
                value={selectedPhaseId}
                onChange={e => handlePhaseSelect(e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              >
                <option value="">Select a phase...</option>
                {phases?.map(p => (
                  <option key={p?.id} value={p?.id}>{p?.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Deliverable Select */}
          {selectedProjectId && assignmentType === 'deliverable' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Deliverable <span className="text-destructive">*</span>
              </label>
              <select
                value={selectedDeliverableId}
                onChange={e => handleDeliverableSelect(e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              >
                <option value="">Select a deliverable...</option>
                {filteredDeliverables?.map(d => (
                  <option key={d?.id} value={d?.id}>{d?.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Start Date <span className="text-destructive">*</span>
              </label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                End Date <span className="text-destructive">*</span>
              </label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e?.target?.value)}
                min={startDate}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <Icon name="AlertCircle" size={16} />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 pb-6">
          <Button variant="outline" onClick={onClose} disabled={loading} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleAssign}
            disabled={loading}
            className="flex-1"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Assigning...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Icon name="UserCheck" size={16} />
                Assign {selectedResources?.length} Resource{selectedResources?.length !== 1 ? 's' : ''}
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignModal;
